import React from "react";
import { type PillarTableBaseColumnProps } from "shared/components/pillar-table/columns/PillarTableColumn";
import PillarTableColumnBoolean from "shared/components/pillar-table/columns/PillarTableColumnBoolean";
import PillarTableColumnCreateModified from "shared/components/pillar-table/columns/PillarTableColumnCreateModified";
import PillarTableColumnDateTime from "shared/components/pillar-table/columns/PillarTableColumnDateTime";
import PillarTableColumnProfileAddress from "shared/components/pillar-table/columns/PillarTableColumnProfileAddress";
import PillarTableColumnProfileContactInformation from "shared/components/pillar-table/columns/PillarTableColumnProfileContactInformation";
import PillarTableColumnProfileDegree from "shared/components/pillar-table/columns/PillarTableColumnProfileDegree";
import PillarTableColumnProfileDisplayName from "shared/components/pillar-table/columns/PillarTableColumnProfileDisplayName";
import PillarTableColumnProfileEmail from "shared/components/pillar-table/columns/PillarTableColumnProfileEmail";
import PillarTableColumnProfileTag from "shared/components/pillar-table/columns/PillarTableColumnProfileTag";
import PillarTableColumnTemplate from "shared/components/pillar-table/columns/PillarTableColumnTemplate";
import PillarTableColumnText from "shared/components/pillar-table/columns/PillarTableColumnText";
import PillarTableColumnTextRegex from "shared/components/pillar-table/columns/PillarTableColumnTextRegex";
import PillarTableColumnWorkflowInstancePossibleActions from "shared/components/pillar-table/columns/PillarTableColumnWorkflowInstancePossibleActions";
import PillarTableColumnWorkflowInstanceStatus from "shared/components/pillar-table/columns/PillarTableColumnWorkflowInstanceStatus";
import { ColumnTypes } from "shared/components/pillar-table/custom-reports/ColumnTypesMapper";
import { type PillarDataTableFixedColumn } from "shared/components/pillar-table/helpers/addFixedStyleToColumn";
import PillarTableNestedTable, {
  type PillarTableNestedTableProps,
} from "shared/components/pillar-table/nested/PillarTableNestedTable";
import { type PillarTableSafeAny } from "shared/components/pillar-table/PillarTableSafeAny";
import { PillarTableColumnArrayCount } from "../columns/array/PillarTableColumnArrayCount";
import { PillarTableColumnArrayFilter } from "../columns/array/PillarTableColumnArrayFilter";
import { PillarTableColumnArrayFind } from "../columns/array/PillarTableColumnArrayFind";
import { PillarTableColumnArrayJoin } from "../columns/array/PillarTableColumnArrayJoin";
import { PillarTableColumnArraySum } from "../columns/array/PillarTableColumnArraySum";

export const customReportComponentsMapper: {
  [ColumnTypes.Text]: typeof PillarTableColumnText;
  [ColumnTypes.TextWithEdit]: typeof PillarTableColumnText.Edit;
  [ColumnTypes.TextNumber]: typeof PillarTableColumnText;
  [ColumnTypes.SelectMenuWithEdit]: typeof PillarTableColumnText.EditSelectMenu;
  [ColumnTypes.Date]: typeof PillarTableColumnDateTime;
  [ColumnTypes.DateWithEdit]: typeof PillarTableColumnDateTime.Edit;
  [ColumnTypes.ProfileDisplayName]: typeof PillarTableColumnProfileDisplayName;
  [ColumnTypes.ProfileEmail]: typeof PillarTableColumnProfileEmail;
  [ColumnTypes.ProfileAddress]: typeof PillarTableColumnProfileAddress;
  [ColumnTypes.ProfileContactInformation]: typeof PillarTableColumnProfileContactInformation;
  [ColumnTypes.ProfileDegree]: typeof PillarTableColumnProfileDegree;
  [ColumnTypes.ProfileTag]: typeof PillarTableColumnProfileTag;
  [ColumnTypes.Boolean]: typeof PillarTableColumnBoolean;
  [ColumnTypes.Template]: typeof PillarTableColumnTemplate;
  [ColumnTypes.WorkflowInstancePossibleActions]: typeof PillarTableColumnWorkflowInstancePossibleActions;
  [ColumnTypes.WorkflowInstanceStatus]: typeof PillarTableColumnWorkflowInstanceStatus;
  [ColumnTypes.ArrayFind]: typeof PillarTableColumnArrayFind;
  [ColumnTypes.ArrayFilter]: typeof PillarTableColumnArrayFilter;
  [ColumnTypes.ArrayJoin]: typeof PillarTableColumnArrayJoin;
  [ColumnTypes.ArrayCount]: typeof PillarTableColumnArrayCount;
  [ColumnTypes.ArraySum]: typeof PillarTableColumnArraySum;
  [ColumnTypes.NestedTable]: (
    props: PillarTableNestedTableProps & PillarTableBaseColumnProps,
  ) => React.ReactElement;
  [ColumnTypes.TextRegexReplace]: typeof PillarTableColumnTextRegex;
  [ColumnTypes.CreateModified]: typeof PillarTableColumnCreateModified;
} = {
  [ColumnTypes.Text]: PillarTableColumnText,
  [ColumnTypes.TextWithEdit]: PillarTableColumnText.Edit,
  // TextNumber renders exactly like Text for now. Someday we will add the
  // development that makes an Excel export write TextNumber cells as numbers
  // instead of text, so report columns that hold numbers must use TextNumber,
  // not Text, to pick that up without being edited.
  [ColumnTypes.TextNumber]: PillarTableColumnText,
  [ColumnTypes.SelectMenuWithEdit]: PillarTableColumnText.EditSelectMenu,
  [ColumnTypes.ProfileDegree]: PillarTableColumnProfileDegree,
  [ColumnTypes.Date]: PillarTableColumnDateTime,
  [ColumnTypes.DateWithEdit]: PillarTableColumnDateTime.Edit,
  [ColumnTypes.ProfileTag]: PillarTableColumnProfileTag,
  [ColumnTypes.ProfileDisplayName]: PillarTableColumnProfileDisplayName,
  [ColumnTypes.ProfileEmail]: PillarTableColumnProfileEmail,
  [ColumnTypes.ProfileAddress]: PillarTableColumnProfileAddress,
  [ColumnTypes.ProfileContactInformation]:
    PillarTableColumnProfileContactInformation,
  [ColumnTypes.Boolean]: PillarTableColumnBoolean,
  [ColumnTypes.Template]: PillarTableColumnTemplate,
  [ColumnTypes.WorkflowInstancePossibleActions]:
    PillarTableColumnWorkflowInstancePossibleActions,
  [ColumnTypes.WorkflowInstanceStatus]: PillarTableColumnWorkflowInstanceStatus,
  [ColumnTypes.ArrayFind]: PillarTableColumnArrayFind,
  [ColumnTypes.ArrayFilter]: PillarTableColumnArrayFilter,
  [ColumnTypes.ArrayJoin]: PillarTableColumnArrayJoin,
  [ColumnTypes.ArrayCount]: PillarTableColumnArrayCount,
  [ColumnTypes.ArraySum]: PillarTableColumnArraySum,
  [ColumnTypes.NestedTable]: PillarTableNestedTable,
  [ColumnTypes.TextRegexReplace]: PillarTableColumnTextRegex,
  [ColumnTypes.CreateModified]: PillarTableColumnCreateModified,
} satisfies Record<
  keyof typeof ColumnTypes,
  React.ComponentType<PillarTableSafeAny>
>;

export type RenderCustomReportColumnFn = (
  config: CustomReportColumnConfig,
  key?: React.Key,
) => React.ReactElement<PillarTableSafeAny> | null;

// Shared helper to render a column component from its config while staying DRY across table, export, and header code paths.
export const renderCustomReportColumn = (
  config: CustomReportColumnConfig,
  key?: React.Key,
): React.ReactElement<PillarTableSafeAny> | null => {
  const ComponentRenderer = customReportComponentsMapper[config.columnType] as
    | React.ComponentType<PillarTableSafeAny>
    | undefined;

  if (!ComponentRenderer) {
    return null;
  }

  return <ComponentRenderer key={key} {...(config as PillarTableSafeAny)} />;
};

export type InternalCustomReportColumnConfig<
  T extends keyof typeof customReportComponentsMapper,
> = React.ComponentProps<(typeof customReportComponentsMapper)[T]> & {
  heading: string;
  columnType: T;
  fixedColumn?: PillarDataTableFixedColumn;
  contentClassName?: string;
};

// 2) build the union of all specializations. A stored nested table carries its
// columns as `nestedTableColumns` config, never as JSX children.
export type CustomReportColumnConfig = {
  [K in keyof typeof customReportComponentsMapper]: K extends typeof ColumnTypes.NestedTable
    ? Omit<InternalCustomReportColumnConfig<K>, "children">
    : InternalCustomReportColumnConfig<K>;
}[keyof typeof customReportComponentsMapper];

/**
 * @deprecated Import ColumnTypes from "shared/components/pillar-table/custom-reports/ColumnTypesMapper" instead.
 */
export { ColumnTypes };
